import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, adminApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const PricingManager = () => {
  const [pricing, setPricing] = useState([]);
  const { toast } = useToast();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const timersRef = useRef(new Map());

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    const response = await api.getPricing();
    setPricing(response.data);
  };

  const hasToken = () => {
    try {
      return Boolean(localStorage.getItem('adminToken'));
    } catch {
      return false;
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      if (!hasToken()) {
        toast({ title: 'Session expirée', description: 'Reconnecte-toi pour modifier les prix.', variant: 'destructive' });
        logout();
        navigate('/admin/login');
        return;
      }
      await adminApi.updatePricing(id, data);
      toast({ title: 'Pricing updated!' });
      loadPricing();
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        toast({ title: 'Non autorisé', description: 'Reconnecte-toi (token expiré).', variant: 'destructive' });
        logout();
        navigate('/admin/login');
        return;
      }
      toast({ title: 'Error', description: error?.response?.data?.detail || 'Update failed', variant: 'destructive' });
    }
  };

  const debouncedUpdate = (id, data) => {
    const key = String(id);
    const prev = timersRef.current.get(key);
    if (prev) window.clearTimeout(prev);
    const t = window.setTimeout(() => handleUpdate(id, data), 550);
    timersRef.current.set(key, t);
  };

  useEffect(() => {
    return () => {
      for (const t of timersRef.current.values()) window.clearTimeout(t);
      timersRef.current.clear();
    };
  }, []);

  const byId = useMemo(() => new Map(pricing.map((p) => [String(p.id), p])), [pricing]);

  return (
    <div className="p-8">
      <h1 className="text-4xl font-bold text-white mb-8">Pricing Plans</h1>
      <div className="grid grid-cols-3 gap-6">
        {pricing.map(plan => (
          <div key={plan.id} className="bg-gradient-to-br from-[#1a1a1b] to-[#0f0f10] rounded-xl border border-cyan-500/10 p-6">
            <input
              value={plan.name}
              className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white mb-2"
              readOnly
            />
            <input
              value={plan.price}
              onChange={(e) => {
                const next = { ...plan, price: e.target.value };
                setPricing((prev) => prev.map((p) => (String(p.id) === String(plan.id) ? next : p)));
                debouncedUpdate(plan.id, next);
              }}
              placeholder="Price"
              className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white mb-2"
            />
            <input
              value={plan.currency}
              onChange={(e) => {
                const next = { ...plan, currency: e.target.value };
                setPricing((prev) => prev.map((p) => (String(p.id) === String(plan.id) ? next : p)));
                debouncedUpdate(plan.id, next);
              }}
              placeholder="Currency (EUR, USD)"
              className="w-full px-4 py-2 bg-[#0a0a0b] border border-cyan-500/20 rounded-lg text-white"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PricingManager;
